import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import UserRoleToggle from "./user-actions/UserRoleToggle";
import BulkUserActions from "./BulkUserActions";
import AdvancedUserFilters from "./AdvancedUserFilters";

interface UserData {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  email: string;
  is_admin: boolean;
  credits_balance: number;
  credits_used: number;
  subscription_status: string;
  subscription_type: string;
  total_token_usage: number;
  monthly_revenue: number;
  total_revenue: number;
}

interface FilterState {
  search: string;
  role: string;
  subscriptionType: string;
  creditsRange: string;
  dateRange: string;
}

interface OptimizedUserManagementProps {
  refreshTrigger?: number;
}

export default function OptimizedUserManagement({ refreshTrigger }: OptimizedUserManagementProps) {
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    role: "",
    subscriptionType: "",
    creditsRange: "",
    dateRange: "",
  });

  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger]);

  // Apply filters whenever users data or filters change
  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const applyFilters = () => {
    let filtered = [...users];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.full_name?.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (filters.role) {
      if (filters.role === "admin") {
        filtered = filtered.filter(user => user.is_admin);
      } else if (filters.role === "user") {
        filtered = filtered.filter(user => !user.is_admin);
      }
    }

    // Subscription type filter
    if (filters.subscriptionType) {
      filtered = filtered.filter(user => user.subscription_type === filters.subscriptionType);
    }

    // Credits range filter
    if (filters.creditsRange) {
      switch (filters.creditsRange) {
        case "0":
          filtered = filtered.filter(user => user.credits_balance === 0);
          break;
        case "1-10":
          filtered = filtered.filter(user => user.credits_balance >= 1 && user.credits_balance <= 10);
          break;
        case "11-50":
          filtered = filtered.filter(user => user.credits_balance >= 11 && user.credits_balance <= 50);
          break;
        case "50+":
          filtered = filtered.filter(user => user.credits_balance > 50);
          break;
      }
    }

    // Date range filter
    if (filters.dateRange) {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (filters.dateRange) {
        case "today":
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case "month":
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case "quarter":
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
      }
      
      if (filters.dateRange !== "") {
        filtered = filtered.filter(user => new Date(user.created_at) >= cutoffDate);
      }
    }

    setFilteredUsers(filtered);
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('get_admin_users_data');
      
      if (error) {
        console.error('Database error:', error);
        if (error.message?.includes('permission denied')) {
          toast.error('Access denied. Admin role required.');
        } else if (error.message?.includes('function get_admin_users_data')) {
          toast.error('Database function error. Please check server logs.');
        } else {
          toast.error(`Failed to load users: ${error.message}`);
        }
        return;
      }
      
      setUsers(data || []);
      console.log('Successfully loaded', data?.length || 0, 'users');
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast.error(`Failed to load users: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      role: "",
      subscriptionType: "",
      creditsRange: "",
      dateRange: "",
    });
    setSelectedUsers([]);
  };

  const exportUsers = () => {
    const csvContent = [
      ['User ID', 'Username', 'Email', 'Full Name', 'Role', 'Credits Balance', 'Credits Used', 'Subscription Type', 'Subscription Status', 'Token Usage', 'Start Date'].join(','),
      ...filteredUsers.map(user => [
        user.id,
        user.username || '',
        user.email,
        user.full_name || '',
        user.is_admin ? 'Admin' : 'User',
        user.credits_balance,
        user.credits_used,
        user.subscription_type,
        user.subscription_status,
        user.total_token_usage,
        new Date(user.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('User data exported successfully');
  };

  const getSubscriptionBadgeVariant = (type: string) => {
    switch (type) {
      case 'yearly': return 'default';
      case 'monthly': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <div className="h-full flex flex-col lg:flex-row gap-6">
      {/* Filters Sidebar - Left Column */}
      <div className="lg:w-80 space-y-4">
        <AdvancedUserFilters 
          filters={filters}
          onFiltersChange={setFilters}
          onReset={resetFilters}
        />
        
        <BulkUserActions
          users={filteredUsers}
          selectedUsers={selectedUsers}
          onSelectionChange={setSelectedUsers}
          onRefresh={fetchUsers}
        />
      </div>

      {/* Main Content - Right Column */}
      <div className="flex-1 min-w-0">
        <Card className="h-full">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Users ({filteredUsers.length})
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={exportUsers}
                  disabled={filteredUsers.length === 0}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchUsers}
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 rounded-full border-2 border-t-transparent border-primary animate-spin"></div>
              </div>
            ) : (
              <div className="rounded-lg border border-t-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedUsers(filteredUsers.map(u => u.id));
                              } else {
                                setSelectedUsers([]);
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>User ID</TableHead>
                        <TableHead>Name/Username</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Token Usage</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedUsers.includes(user.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedUsers([...selectedUsers, user.id]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {user.id.slice(0, 8)}...
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {user.full_name || user.username || "No Name"}
                              </span>
                              {user.username && user.full_name && (
                                <span className="text-xs text-muted-foreground">
                                  @{user.username}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{user.email}</span>
                              {user.email === 'N/A' && (
                                <span className="text-xs text-red-500">No email</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.is_admin ? "default" : "secondary"}>
                              {user.is_admin ? "Admin" : "User"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{user.credits_balance}</span>
                              <span className="text-xs text-muted-foreground">
                                Used: {user.credits_used}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getSubscriptionBadgeVariant(user.subscription_type)}>
                              {user.subscription_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={user.subscription_status === 'active' ? 'default' : 'destructive'}
                            >
                              {user.subscription_status === 'active' ? 'Active' : 'Cancelled'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {user.total_token_usage.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{new Date(user.created_at).toLocaleDateString()}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(user.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <UserRoleToggle 
                              userId={user.id}
                              isAdmin={user.is_admin}
                              onUpdate={fetchUsers}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
